import _createForOfIteratorHelper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
import { first } from 'ckeditor5/src/utils.js';
/**
 * @module image/imagestyle/converters
 */
/**
 * Returns a converter for the `imageStyle` attribute. It can be used for adding, changing and removing the attribute.
 *
 * @param styles An array containing available image style options.
 * @returns A model-to-view attribute converter.
 */
export function modelToViewStyleAttribute(styles) {
  return function (evt, data, conversionApi) {
    if (!conversionApi.consumable.consume(data.item, evt.name)) {
      return;
    }
    // Check if there is class name associated with given value.
    var newStyle = getStyleDefinitionByName(data.attributeNewValue, styles);
    var oldStyle = getStyleDefinitionByName(data.attributeOldValue, styles);
    var viewElement = conversionApi.mapper.toViewElement(data.item);
    var viewWriter = conversionApi.writer;
    if (oldStyle) {
      viewWriter.removeClass(oldStyle.className, viewElement);
    }
    if (newStyle) {
      viewWriter.addClass(newStyle.className, viewElement);
    }
  };
}
/**
 * Returns a view-to-model converter converting image CSS classes to a proper value in the model.
 *
 * @param styles Image style options for which the converter is created.
 * @returns A view-to-model converter.
 */
export function viewToModelStyleAttribute(styles) {
  // Convert only non–default styles.
  var nonDefaultStyles = {
    imageInline: styles.filter(function (style) {
      return !style.isDefault && style.modelElements.includes('imageInline');
    }),
    imageBlock: styles.filter(function (style) {
      return !style.isDefault && style.modelElements.includes('imageBlock');
    })
  };
  return function (evt, data, conversionApi) {
    if (!data.modelRange) {
      return;
    }
    var viewElement = data.viewItem;
    var modelImageElement = first(data.modelRange.getItems());
    // Run this converter only if an image has been found in the model.
    // In some cases it may not be found (for example if we run this on a figure with different type than image).
    if (!modelImageElement) {
      return;
    }
    // ...and the `imageStyle` attribute is allowed for that element, otherwise stop conversion early.
    if (!conversionApi.schema.checkAttribute(modelImageElement, 'imageStyle')) {
      return;
    }
    // Convert styles one by one.
    var _iterator = _createForOfIteratorHelper(nonDefaultStyles[modelImageElement.name]),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var style = _step.value;
        // Try to consume class corresponding with the style.
        if (conversionApi.consumable.consume(viewElement, {
          classes: style.className
        })) {
          // And convert this style to model attribute.
          conversionApi.writer.setAttribute('imageStyle', style.name, modelImageElement);
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  };
}
/**
 * Returns the style with a given `name` from an array of styles.
 */
function getStyleDefinitionByName(name, styles) {
  var _iterator2 = _createForOfIteratorHelper(styles),
    _step2;
  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var style = _step2.value;
      if (style.name === name) {
        return style;
      }
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }
}